import type { EncryptedObject } from '@trove/contracts';
import type { RepositoryFile, RepositorySnapshot } from './types';
import { repositoryFileBytes } from './bytes';

export const MANIFEST_FORMAT = 1 as const;

export interface ManifestFile {
  path: string;
  mimeType: string;
  size: number;
  modifiedAt: string;
  blobOpaqueId: string;
  contentHash: string;
}

export interface ManifestCommit {
  id: string;
  treeId: string;
  parentId: string | null;
  message: string;
  author: string;
  authorEmail: string;
  committer: string;
  committerEmail: string;
  createdAt: string;
  authorTimestamp?: number;
  authorTimezone?: string;
  committerTimestamp?: number;
  committerTimezone?: string;
  files: ManifestFile[];
}

/** Content-addressed repository head (replaces monolithic snapshot encryption). */
export interface RepositoryManifest {
  format: typeof MANIFEST_FORMAT;
  repositoryId: string;
  collectionId: string;
  version: number;
  head: string;
  files: ManifestFile[];
  commits: ManifestCommit[];
}

export interface BlobPayload {
  mimeType: string;
  content: string;
}

const encoder = new TextEncoder();

const hex = (bytes: Uint8Array) =>
  [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

export const contentHashOf = async (
  file: Pick<RepositoryFile, 'content' | 'mimeType'>,
): Promise<string> => {
  if (file.content === undefined) throw new Error('Cannot hash a file without content');
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', repositoryFileBytes(file) as BufferSource),
  );
  return hex(digest);
};

export const isRepositoryManifest = (value: unknown): value is RepositoryManifest => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    record.format === MANIFEST_FORMAT &&
    Array.isArray(record.files) &&
    Array.isArray(record.commits)
  );
};

export const isFileContentLoaded = (file: Pick<RepositoryFile, 'content' | 'size'>): boolean =>
  file.content !== undefined;

export const needsBlobFetch = (
  file: Pick<RepositoryFile, 'content' | 'size' | 'blobOpaqueId'>,
): boolean => Boolean(file.blobOpaqueId && file.content === undefined);

export function fileRef(file: RepositoryFile): ManifestFile {
  if (!file.blobOpaqueId || !file.contentHash) {
    throw new Error(`File ${file.path} is missing blob addressing`);
  }
  return {
    path: file.path,
    mimeType: file.mimeType,
    size: file.size,
    modifiedAt: file.modifiedAt,
    blobOpaqueId: file.blobOpaqueId,
    contentHash: file.contentHash,
  };
}

export function snapshotFromManifest(manifest: RepositoryManifest): RepositorySnapshot {
  return {
    repositoryId: manifest.repositoryId,
    collectionId: manifest.collectionId,
    version: manifest.version,
    head: manifest.head,
    files: manifest.files.map((file) => ({
      path: file.path,
      mimeType: file.mimeType,
      size: file.size,
      modifiedAt: file.modifiedAt,
      blobOpaqueId: file.blobOpaqueId,
      contentHash: file.contentHash,
    })),
    commits: manifest.commits.map((commit) => ({
      id: commit.id,
      treeId: commit.treeId,
      parentId: commit.parentId,
      message: commit.message,
      author: commit.author,
      authorEmail: commit.authorEmail,
      committer: commit.committer,
      committerEmail: commit.committerEmail,
      createdAt: commit.createdAt,
      authorTimestamp: commit.authorTimestamp,
      authorTimezone: commit.authorTimezone,
      committerTimestamp: commit.committerTimestamp,
      committerTimezone: commit.committerTimezone,
      files: commit.files.map((file) => ({
        path: file.path,
        mimeType: file.mimeType,
        size: file.size,
        modifiedAt: file.modifiedAt,
        blobOpaqueId: file.blobOpaqueId,
        contentHash: file.contentHash,
      })),
    })),
  };
}

export function encodeBlobPayload(file: Pick<RepositoryFile, 'mimeType' | 'content'>): Uint8Array {
  if (file.content === undefined) throw new Error('Cannot encode a blob without content');
  const payload: BlobPayload = { mimeType: file.mimeType, content: file.content };
  return encoder.encode(JSON.stringify(payload));
}

export function decodeBlobPayload(bytes: Uint8Array): BlobPayload {
  const payload = JSON.parse(new TextDecoder().decode(bytes)) as BlobPayload;
  if (typeof payload.mimeType !== 'string' || typeof payload.content !== 'string') {
    throw new Error('Invalid blob payload');
  }
  return payload;
}

export function encodeManifest(manifest: RepositoryManifest): Uint8Array {
  return encoder.encode(JSON.stringify(manifest));
}

type EncryptFn = (plaintext: Uint8Array, kind: EncryptedObject['kind']) => Promise<EncryptedObject>;

/** Encrypt unique file blobs + a manifest. Reuses blob opaque ids when contentHash matches. */
export async function packSnapshotObjects(
  snapshot: RepositorySnapshot,
  encrypt: EncryptFn,
): Promise<{
  objects: EncryptedObject[];
  manifest: RepositoryManifest;
  snapshot: RepositorySnapshot;
}> {
  const hashToOpaque = new Map<string, string>();
  const objects: EncryptedObject[] = [];

  for (const file of [...snapshot.files, ...snapshot.commits.flatMap((commit) => commit.files)]) {
    if (file.contentHash && file.blobOpaqueId)
      hashToOpaque.set(file.contentHash, file.blobOpaqueId);
  }

  const ensureBlob = async (file: RepositoryFile): Promise<ManifestFile> => {
    if (file.content === undefined) {
      if (file.blobOpaqueId && file.contentHash) return fileRef(file);
      throw new Error(`File ${file.path} has no content and no blob reference`);
    }
    const contentHash = file.contentHash ?? (await contentHashOf(file));
    const reusable =
      (file.blobOpaqueId && file.contentHash === contentHash ? file.blobOpaqueId : undefined) ??
      hashToOpaque.get(contentHash);
    if (reusable) {
      hashToOpaque.set(contentHash, reusable);
      return {
        path: file.path,
        mimeType: file.mimeType,
        size: file.size,
        modifiedAt: file.modifiedAt,
        blobOpaqueId: reusable,
        contentHash,
      };
    }
    const encrypted = await encrypt(encodeBlobPayload(file), 'blob');
    objects.push(encrypted);
    hashToOpaque.set(contentHash, encrypted.objectId);
    return {
      path: file.path,
      mimeType: file.mimeType,
      size: file.size,
      modifiedAt: file.modifiedAt,
      blobOpaqueId: encrypted.objectId,
      contentHash,
    };
  };

  const headFiles: ManifestFile[] = [];
  for (const file of snapshot.files) headFiles.push(await ensureBlob(file));

  const commits: ManifestCommit[] = [];
  for (const commit of snapshot.commits) {
    const files: ManifestFile[] = [];
    for (const file of commit.files) files.push(await ensureBlob(file));
    commits.push({
      id: commit.id,
      treeId: commit.treeId,
      parentId: commit.parentId,
      message: commit.message,
      author: commit.author,
      authorEmail: commit.authorEmail,
      committer: commit.committer,
      committerEmail: commit.committerEmail,
      createdAt: commit.createdAt,
      authorTimestamp: commit.authorTimestamp,
      authorTimezone: commit.authorTimezone,
      committerTimestamp: commit.committerTimestamp,
      committerTimezone: commit.committerTimezone,
      files,
    });
  }

  const manifest: RepositoryManifest = {
    format: MANIFEST_FORMAT,
    repositoryId: snapshot.repositoryId,
    collectionId: snapshot.collectionId,
    version: snapshot.version,
    head: snapshot.head,
    files: headFiles,
    commits,
  };

  const manifestObject = await encrypt(encodeManifest(manifest), 'manifest');
  objects.push(manifestObject);

  const addressed = snapshotFromManifest(manifest);
  // Keep head working-tree content in memory when we already have it.
  const contentByPath = new Map(
    snapshot.files
      .filter((file) => file.content !== undefined)
      .map((file) => [file.path, file.content!]),
  );
  return {
    objects,
    manifest,
    snapshot: {
      ...addressed,
      files: addressed.files.map((file) =>
        contentByPath.has(file.path) ? { ...file, content: contentByPath.get(file.path) } : file,
      ),
    },
  };
}

export async function hydrateFilesFromBlobs(
  files: RepositoryFile[],
  readBlobs: (opaqueIds: string[]) => Promise<Map<string, Uint8Array>>,
): Promise<RepositoryFile[]> {
  const missing = files.filter(needsBlobFetch);
  if (!missing.length) return files;
  const ids = [...new Set(missing.map((file) => file.blobOpaqueId!))];
  const blobs = await readBlobs(ids);
  return files.map((file) => {
    if (!needsBlobFetch(file)) return file;
    const bytes = blobs.get(file.blobOpaqueId!);
    if (!bytes) throw new Error(`Missing blob ${file.blobOpaqueId} for ${file.path}`);
    const payload = decodeBlobPayload(bytes);
    return {
      ...file,
      mimeType: payload.mimeType,
      content: payload.content,
      size: encoder.encode(payload.content).length,
    };
  });
}

export async function hydrateSnapshotHead(
  snapshot: RepositorySnapshot,
  readBlobs: (opaqueIds: string[]) => Promise<Map<string, Uint8Array>>,
): Promise<RepositorySnapshot> {
  return {
    ...snapshot,
    files: await hydrateFilesFromBlobs(snapshot.files, readBlobs),
  };
}
